import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import PropTypes from "prop-types";
import ReactSelect, { components, createFilter } from "react-select";
import AsyncSelect from "react-select/async";

import styles from "./select.styles";

import cssStyles from "./Select.module.scss";

const Select = ({
  isMulti,
  hasGroup,
  name,
  options,
  optionsKey,
  optionsLabel,
  customStyles,
  selectedDefault,
  isLoading,
  loadingClassName,
  onChange,
  async,
  loadOptions,
  arrowIcon,
  isClearable,
  ...rest
}) => {
  const DropdownIndicator = (props) => {
    if (arrowIcon) {
      return (
        <components.DropdownIndicator {...props}>
          <FontAwesomeIcon icon={arrowIcon} className={cssStyles.arrowIcon} />
        </components.DropdownIndicator>
      );
    }

    return <components.DropdownIndicator {...props} />;
  };

  const Option = ({ children, ..._props }) => (
    <components.Option {..._props} className="select-option">
      {_props.data.backgroundColor ? (
        <span
          className={cssStyles.option}
          style={{
            backgroundColor: _props.data.backgroundColor,
            color: "#fff",
          }}
        >
          {children}
        </span>
      ) : (
        children
      )}
      {_props.data.description && (
        <span className={cssStyles.description}>{_props.data.description}</span>
      )}
    </components.Option>
  );

  const SingleValue = (props) => {
    const backgroundColor =
      options.find((o) => o.value === props.data.value)?.backgroundColor ?? "";

    return (
      <components.SingleValue {...props} className="single-value">
        {props.data.icon && (
          <FontAwesomeIcon icon={props.data.icon} className={cssStyles.selectedOptionIcon} />
        )}
        {backgroundColor ? (
          <span
            className={cssStyles.option}
            style={backgroundColor ? { backgroundColor, color: "#fff" } : {}}
          >
            {props.data.label}
          </span>
        ) : (
          props.data.label
        )}
      </components.SingleValue>
    );
  };

  const MultiValue = (props) => {
    const value = options.find((o) => o[optionsKey] === props.data.value);
    return <components.MultiValue {...props}>{value.label}</components.MultiValue>;
  };

  const MultiValueRemove = (props) => {
    return <components.MultiValueRemove {...props}>&nbsp;</components.MultiValueRemove>;
  };

  const MultiValueContainer = ({ children, ...props }) => {
    return <components.MultiValueContainer {...props}>{children}</components.MultiValueContainer>;
  };

  const GroupHeading = (props) => (
    <div>
      <components.GroupHeading {...props} />
    </div>
  );

  const SelectContainer = ({ children, ...props }) => (
    <components.SelectContainer
      {...props}
      innerProps={{ ...props.innerProps, "data-testid": "select" }}
    >
      {children}
    </components.SelectContainer>
  );

  const Control = ({ children, ...props }) => (
    <components.Control
      {...props}
      innerProps={{
        ...props.innerProps,
        "data-testid": "select-control",
        className: "select-control",
      }}
    >
      {children}
    </components.Control>
  );

  const MenuList = ({ children, ...props }) => (
    <components.MenuList
      {...props}
      innerProps={{
        ...props.innerProps,
        "data-testid": "select-menu-list",
        className: "select-menu-list",
      }}
    >
      {children}
    </components.MenuList>
  );

  const Placeholder = ({ children, ...props }) => (
    <components.Placeholder
      {...props}
      innerProps={{
        ...props.innerProps,
        "data-testid": "select-placeholder",
        className: "select-placeholder",
      }}
    >
      {children}
    </components.Placeholder>
  );

  const flattenOptions = (opts) => {
    return opts.flatMap((item) => (item.options ? [item, ...flattenOptions(item.options)] : item));
  };

  const value = isMulti
    ? flattenOptions(options)
        .filter((option) => selectedDefault.includes(option[optionsKey]))
        .map((option) => ({
          value: option[optionsKey],
          label: option[optionsLabel],
        }))
    : flattenOptions(options)
        .filter((option) => option[optionsKey] === selectedDefault)
        .map((option) => ({
          value: option[optionsKey],
          label: option[optionsLabel],
        }))[0] || null;

  if (isLoading) {
    return <div className={cx(cssStyles.loadingWrapper, loadingClassName)}>Loading...</div>;
  }

  if (async) {
    return (
      <AsyncSelect
        defaultOptions
        cacheOptions
        styles={{ ...styles, ...customStyles }}
        value={selectedDefault}
        loadOptions={loadOptions}
        onChange={onChange}
      />
    );
  }

  return (
    <ReactSelect
      isMulti={isMulti}
      closeMenuOnSelect={!isMulti}
      captureMenuScroll={false}
      key={`select-key-${name}-${selectedDefault}`}
      styles={{ ...styles, ...customStyles }}
      filterOption={createFilter({ ignoreAccents: false })}
      isClearable={isClearable}
      options={
        hasGroup
          ? options.map((option) => ({
              label: option.label,
              options: option.options.map((option) => ({
                value: option[optionsKey],
                label: option[optionsLabel],
                description: option?.description ?? "",
              })),
            }))
          : options.map((option) => ({
              value: option[optionsKey],
              label: option[optionsLabel],
              description: option?.description ?? "",
              backgroundColor: option?.backgroundColor ?? "",
            }))
      }
      components={{
        MenuList,
        Placeholder,
        Control,
        SelectContainer,
        DropdownIndicator,
        Option,
        GroupHeading,
        SingleValue,
        MultiValue,
        MultiValueContainer,
        MultiValueRemove,
      }}
      defaultValue={value}
      onChange={(newValue) => {
        if (isMulti) {
          onChange(newValue);
        } else if (newValue === null) {
          onChange({});
        } else {
          onChange(flattenOptions(options).find((option) => option[optionsKey] === newValue.value));
        }
      }}
      isLoading={isLoading}
      placeholder={isLoading ? "Loading..." : "Select..."}
      {...rest}
    />
  );
};

Select.defaultProps = {
  isMulti: false,
  hasGroup: false,
  optionsKey: "value",
  optionsLabel: "label",
  options: [],
  selectedDefault: null,
  customStyles: {},
  isLoading: false,
  loadingClassName: "",
  onChange: () => null,
  isClearable: false,
};

Select.propTypes = {
  isMulti: PropTypes.bool,
  hasGroup: PropTypes.bool,
  name: PropTypes.string.isRequired,
  optionsKey: PropTypes.string,
  optionsLabel: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ),
  selectedDefault: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({}), PropTypes.number]),
  customStyles: PropTypes.shape({}),
  isLoading: PropTypes.bool,
  loadingClassName: PropTypes.string,
  onChange: PropTypes.func,
  isClearable: PropTypes.bool,
};

export default Select;
